import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import { copyETLDryRunObjectTableColumns } from '../utils';
import {
  addDryRunObjectTable,
  getETLComponentInputObjectTable,
  getETLTableObjectColumnDataTypeIfPresent,
  getETLTableObjectColumnOptional,
} from '../../utils';

import styles from '../../edit-etl-component/editETLComponentForm.module.scss';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { TpFilterInstance } from '../../filters/interfaces';
import { get_guid } from '../../../visual-analytics/helpers/misc';
import {
  getDefaultFilterType,
  filterHasCompatibleSourceColumn,
  filterTypesList,
  getFilterTypeInfo,
} from '../../filters/filterTypes';
import { getDryRunErrorsForComponent } from '../../etlDryRun';
import ParamSet from '../../edit-etl-component/ParamSet';

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const inputTableObj = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');
  const outputTableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['result'], comp);
  copyETLDryRunObjectTableColumns(inputTableObj, outputTableObj);

  // Check filters for validity
  const filters = (comp.params['filters'] || []) as TpFilterInstance[];
  filters.forEach((filter) => {
    const filterType = getFilterTypeInfo(filter.filterType);
    filterType.performDryRun({ dryRun, comp, filter });
  });

  return {
    abort: false,
  };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
): any {
  const sourceTable = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');

  if (sourceTable.columns.length == 0) return null;

  // Contains the list of aggregations to be performed. Each aggregation takes a source column and produces an output column
  const filters = (comp.params['filters'] || []) as TpFilterInstance[];

  function updateFilterParam(filterId: string, paramId: string, newValue: any) {
    const filterIdx = filters.map((filter) => filter.id).indexOf(filterId);
    if (filterIdx < 0) throw `Invalid filter id ${filterId}`;
    if (paramId == 'colId') {
      const colId = newValue;
      filters[filterIdx].colId = colId;
      filters[filterIdx].value = '';
      filters[filterIdx].valueType = getETLTableObjectColumnDataTypeIfPresent(sourceTable, colId);
      const colInfo = getETLTableObjectColumnOptional(sourceTable, colId);
      if (colInfo) {
        const currentFilterType = getFilterTypeInfo(filters[filterIdx].filterType);
        if (!filterHasCompatibleSourceColumn(currentFilterType.id, colInfo.dataType)) {
          filters[filterIdx].filterType = getDefaultFilterType(colInfo.dataType).id;
        }
      }
    } else if (paramId == 'filterType') {
      filters[filterIdx].filterType = newValue;
      filters[filterIdx].value = '';
    } else if (paramId == 'value') {
      filters[filterIdx].value = newValue;
    }
    updateEditingComponent(comp);
  }

  function renderFilterErrors(filter: TpFilterInstance) {
    const errors = getDryRunErrorsForComponent(dryRun, comp.id).filter((error) => error.paramId == filter.id);
    if (errors.length == 0) return null;
    return (
      <div className={styles.errors}>
        {errors.map((error, idx) => (
          <div key={`errors_${filter.id}_${idx}`}>{error.message}</div>
        ))}
      </div>
    );
  }

  return (
    <>
      {filters.map((filter) => (
        <div key={`filter_${filter.id}`} className={styles.section}>
          <h3>
            Filter
            <button
              onClick={() => {
                comp.params['filters'] = filters.filter((f) => filter.id !== f.id);
                updateEditingComponent(comp);
              }}
            >
              <FontAwesomeIcon icon="xmark" />
            </button>
          </h3>
          {renderFilterErrors(filter)}

          <ParamSet
            params={[
              {
                name: 'Source column',
                component: (
                  <select
                    value={filter.colId}
                    onChange={(ev) => {
                      updateFilterParam(filter.id, 'colId', ev.target.value);
                    }}
                  >
                    <option key="_empty_" value={''}>
                      - Select -
                    </option>
                    {sourceTable.columns.map((col) => (
                      <option key={col.identifier} value={col.identifier}>
                        {col.name}
                      </option>
                    ))}
                  </select>
                ),
              },
              {
                name: 'Filter type',
                component: (
                  <>
                    <select
                      value={filter.filterType}
                      onChange={(ev) => {
                        updateFilterParam(filter.id, 'filterType', ev.target.value);
                      }}
                    >
                      {filterTypesList
                        .filter((filterType) =>
                          filterHasCompatibleSourceColumn(
                            filterType.id,
                            getETLTableObjectColumnDataTypeIfPresent(sourceTable, filter.colId),
                          ),
                        )
                        .map((aggType) => (
                          <option key={aggType.id} value={aggType.id}>
                            {aggType.name}
                          </option>
                        ))}
                    </select>
                    <div style={{ marginTop: '3px' }}>
                      {filter.filterType && getFilterTypeInfo(filter.filterType).description}
                    </div>
                  </>
                ),
              },
              {
                name: 'Value',
                component: (
                  <input
                    type="text"
                    value={filter.value}
                    onChange={(ev) => {
                      updateFilterParam(filter.id, 'value', ev.target.value);
                    }}
                  />
                ),
              },
            ]}
          />
        </div>
      ))}

      <FancyButton
        iconName="fas:plus"
        onClick={() => {
          const defaultCol = sourceTable.columns[0];
          const newFilter: TpFilterInstance = {
            id: get_guid(),
            colId: defaultCol.identifier,
            filterType: getDefaultFilterType(defaultCol.dataType).id,
            value: '',
            valueType: sourceTable.columns[0].dataType,
          };
          comp.params['filters'] = [...filters, newFilter];
          updateEditingComponent(comp);
        }}
      >
        Add Filter
      </FancyButton>
    </>
  );
}

export const manifestETLFilterTable: TpETLComponentTypeManifest = {
  id: 'FilterTable',
  name: 'Filter Table',
  description: 'Only retains those row in the table that fulfill a condition',
  categoryId: 'table',
  icon: <FontAwesomeIcon icon={['fas', 'filter']} />,
  inputs: [
    {
      type: TpETLObjectTypes.TABLE,
      id: 'sourceTable',
      name: 'Source Table',
      columns: [],
    },
  ],
  outputs: [
    {
      id: 'result',
      name: 'Result table',
      type: TpETLObjectTypes.TABLE,
      description: 'Table with the filtered rows',
      columns: [],
    },
  ],
  params: [],
  performDryRun,
  renderCustomConfigParams,
};
